import React, { useCallback, useMemo } from 'react';
import { Box, IconButton, Link, Stack, Tooltip, Typography } from '@mui/material';
import { GridColDef, GridColumnHeaderParams } from '@mui/x-data-grid-premium';
import { ConditionsNew, ConditionState } from '@retail/calculus/types';
import { useAppTFunction } from '@retail/app/i18n';
import { getConditionBindingId } from '@retail/calculus/utils';
import { DATA_GRID_ID } from '@retail/utils';
import { baseColumnProps, getFreightText, resolveCondition } from './utils';
import { EmptyCell } from './EmptyCell';
import { ConditionCell } from './ConditionCell';
import { SalesPriceConditionValue } from '../SalesPriceConditions';
import { ConditionTooltipInfo } from '../ConditionTooltipInfo';
import { InheritedConditionTag } from './InheritedConditionTag';
import { PurchaseConditionInfo } from './PurchaseConditionInfo';
import { AgreementBinding } from '@retail/purchase-agreements/types';
import { RoundingConditionInfo } from './RoundingConditionInfo';
import PriceChangeOutlinedIcon from '@mui/icons-material/PriceChangeOutlined';
import { ConditionsTreeDataGrid } from '../ConditionsTreeDataGrid';
import { ConditionsTableActions } from '../ConditionsTableActions';

interface Props {
  conditions: ConditionsNew[];
  setPriceComparisonBinding: (binding?: AgreementBinding) => void;
  openPriceSimulation: (roundingConditionId: number) => void;
  editCondition?: (row: ConditionsNew) => void;
  deactivateCondition?: (conditionIds: number[]) => void;
  deleteCondition?: (conditionIds: number[]) => void;
  language: string;
  loading: boolean;
  conditionState?: ConditionState;
  showRounding?: boolean;
}

const renderColumnHeader = ({ colDef }: GridColumnHeaderParams<ConditionsNew>) => (
  <Typography variant="subtitle2" component="span">
    {colDef.headerName}
  </Typography>
);

export const AllConditionsTable = ({
  conditions,
  setPriceComparisonBinding,
  openPriceSimulation,
  editCondition,
  deactivateCondition,
  deleteCondition,
  language,
  loading,
  conditionState,
  showRounding = true,
}: Props) => {
  const t = useAppTFunction();

  const showFutureIndicator = conditionState === 'ACTIVE_AND_FUTURE';

  const conditionActionProps = useCallback(
    (row: ConditionsNew, conditionId: number) =>
      row.inDraft
        ? { onDelete: deleteCondition ? () => deleteCondition([conditionId]) : undefined }
        : {
            onDeactivate: deactivateCondition
              ? () => deactivateCondition([conditionId])
              : undefined,
          },
    [deactivateCondition, deleteCondition]
  );

  const columns: GridColDef<ConditionsNew>[] = useMemo(() => {
    const allColumns: GridColDef<ConditionsNew>[] = [
      {
        ...baseColumnProps,
        renderHeader: renderColumnHeader,
        field: 'purchase',
        headerName: t('purchaseAgreements.agreement.columns.agreement'),
        width: 200,
        renderCell: (params) => {
          const condition = resolveCondition(params);
          if (!condition) return null;
          const purchaseCondition = condition.purchaseCondition;
          if (!purchaseCondition) return <EmptyCell />;
          if (purchaseCondition.isInherited) {
            return (
              <ConditionCell
                validFrom={purchaseCondition.validFrom}
                validTo={purchaseCondition.validTo}
                showFutureIndicator={showFutureIndicator}
                tooltipHeader={
                  <Stack gap={1}>
                    <ConditionTooltipInfo
                      title={t('calculus.inheritedCondition')}
                      bindingType={purchaseCondition.inheritedBinding?.bindingType}
                      bindingTarget={`${purchaseCondition.inheritedBinding?.bindingTargetName}, ${purchaseCondition.inheritedBinding?.bindingTargetId}`}
                    />
                    <Stack direction="row" gap={1} alignItems="start">
                      <Typography variant="body2">
                        {t('purchaseAgreements.agreement.columns.agreement')}:
                      </Typography>
                      <PurchaseConditionInfo purchaseCondition={purchaseCondition} />
                    </Stack>
                  </Stack>
                }
              >
                <InheritedConditionTag />
              </ConditionCell>
            );
          }
          return (
            <ConditionCell
              validFrom={purchaseCondition.validFrom}
              validTo={purchaseCondition.validTo}
              showFutureIndicator={showFutureIndicator}
              {...conditionActionProps(params.row, purchaseCondition.conditionId)}
            >
              <Stack alignItems="flex-start">
                <PurchaseConditionInfo purchaseCondition={purchaseCondition} />

                <Link
                  component="button"
                  disabled={!params.row.supplier}
                  onClick={() =>
                    setPriceComparisonBinding({
                      ...params.row.getBindingLevel(),
                      supplierNumber: params.row.supplier?.supplierNumber,
                      agreementId: params.row.purchaseCondition?.agreementId,
                    })
                  }
                  fontWeight={600}
                >
                  {t('purchaseAgreements.priceList.lookUpPrices')}
                </Link>
              </Stack>
            </ConditionCell>
          );
        },
      },
      {
        ...baseColumnProps,
        renderHeader: renderColumnHeader,
        field: 'freight',
        headerName: t('freight.columns.freightValue'),
        width: 160,
        renderCell: (params) => {
          const condition = resolveCondition(params);
          if (!condition) return null;
          const freightCondition = condition.freightCondition;
          if (!freightCondition) return <EmptyCell />;
          if (freightCondition.isInherited) {
            return (
              <ConditionCell
                validFrom={freightCondition.validFrom}
                validTo={freightCondition.validTo}
                showFutureIndicator={showFutureIndicator}
                tooltipHeader={
                  <Stack gap={1}>
                    <ConditionTooltipInfo
                      title={t('calculus.inheritedCondition')}
                      bindingType={freightCondition.inheritedBinding?.bindingType}
                      bindingTarget={`${freightCondition.inheritedBinding?.bindingTargetName}, ${freightCondition.inheritedBinding?.bindingTargetId}`}
                    />
                    <Stack direction="row" gap={1} alignItems="start">
                      <Typography variant="body2">{t('freight.columns.freightValue')}:</Typography>
                      {getFreightText(condition, t)}
                    </Stack>
                  </Stack>
                }
              >
                <InheritedConditionTag />
              </ConditionCell>
            );
          }
          return (
            <ConditionCell
              validFrom={freightCondition.validFrom}
              validTo={freightCondition.validTo}
              showFutureIndicator={showFutureIndicator}
              {...conditionActionProps(params.row, freightCondition.conditionId)}
            >
              <Typography variant="subtitle2" component="span" color="text.primary">
                {getFreightText(condition, t)}
              </Typography>
            </ConditionCell>
          );
        },
      },
      {
        ...baseColumnProps,
        renderHeader: renderColumnHeader,
        field: 'salesPrice',
        headerName: t('calculus.salesPriceConditions.priority'),
        width: 200,
        renderCell: (params) => {
          const condition = resolveCondition(params);
          if (!condition) return null;
          const priceSourceCondition = condition.priceSourceCondition;
          if (!priceSourceCondition) return <EmptyCell />;
          if (priceSourceCondition.isInherited) {
            return (
              <ConditionCell
                validFrom={priceSourceCondition.validFrom}
                validTo={priceSourceCondition.validTo}
                showFutureIndicator={showFutureIndicator}
                tooltipHeader={
                  <Stack>
                    <ConditionTooltipInfo
                      title={t('calculus.inheritedCondition')}
                      bindingType={priceSourceCondition.inheritedBinding?.bindingType}
                      bindingTarget={`${priceSourceCondition.inheritedBinding?.bindingTargetName}, ${priceSourceCondition.inheritedBinding?.bindingTargetId}`}
                    />
                    <SalesPriceConditionValue
                      condition={{
                        ...condition,
                        markupCondition: condition.markupCondition
                          ? { ...condition.markupCondition, isInherited: false }
                          : undefined,
                      }}
                    />
                  </Stack>
                }
              >
                <Box pl={2}>
                  <InheritedConditionTag />
                </Box>
              </ConditionCell>
            );
          }
          return (
            <ConditionCell
              validFrom={priceSourceCondition.validFrom}
              validTo={priceSourceCondition.validTo}
              showFutureIndicator={showFutureIndicator}
              {...conditionActionProps(params.row, priceSourceCondition.conditionId)}
            >
              <SalesPriceConditionValue condition={condition} />
            </ConditionCell>
          );
        },
      },
      {
        ...baseColumnProps,
        renderHeader: renderColumnHeader,
        field: 'rounding',
        headerName: t('calculus.rounding.ruleSetName'),
        width: 200,
        renderCell: (params) => {
          const condition = resolveCondition(params);
          if (!condition) return null;
          const roundingCondition = condition.roundingCondition;
          if (!roundingCondition) return <EmptyCell />;
          if (roundingCondition.isInherited) {
            return (
              <ConditionCell
                validFrom={roundingCondition.validFrom}
                validTo={roundingCondition.validTo}
                showFutureIndicator={showFutureIndicator}
                tooltipHeader={
                  <Stack gap={1}>
                    <ConditionTooltipInfo
                      title={t('calculus.inheritedCondition')}
                      bindingType={roundingCondition.inheritedBinding?.bindingType}
                      bindingTarget={`${roundingCondition.inheritedBinding?.bindingTargetName}, ${roundingCondition.inheritedBinding?.bindingTargetId}`}
                    />
                    <Stack direction="row" gap={1} alignItems="start">
                      <Typography variant="body2">{t('calculus.rounding.ruleSetName')}:</Typography>
                      <RoundingConditionInfo roundingCondition={roundingCondition} />
                    </Stack>
                  </Stack>
                }
              >
                <InheritedConditionTag />
              </ConditionCell>
            );
          }
          return (
            <ConditionCell
              validFrom={roundingCondition.validFrom}
              validTo={roundingCondition.validTo}
              showFutureIndicator={showFutureIndicator}
              {...conditionActionProps(params.row, roundingCondition.conditionId)}
            >
              <Stack gap={1} flexDirection="row" flexWrap="wrap" alignItems="center">
                <RoundingConditionInfo roundingCondition={roundingCondition} />
                <Tooltip title={t('rounding.simulation.title')}>
                  <IconButton
                    onClick={() => openPriceSimulation(roundingCondition.ruleSetId)}
                    color="primary"
                    size="small"
                  >
                    <PriceChangeOutlinedIcon sx={{ width: 24, height: 24 }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            </ConditionCell>
          );
        },
      },
    ];
    return showRounding ? allColumns : allColumns.filter((col) => col.field !== 'rounding');
  }, [
    conditionActionProps,
    openPriceSimulation,
    setPriceComparisonBinding,
    showFutureIndicator,
    showRounding,
    t,
  ]);

  return (
    <ConditionsTreeDataGrid
      dataGridId={DATA_GRID_ID.CALCULUS_ALL_CONDITIONS_TABLE}
      rows={conditions}
      getRowId={getConditionBindingId}
      columns={columns}
      language={language}
      loading={loading}
      noRowsText={t('calculus.allConditions.noConditions')}
      getAssortmentLabel={(row) => row.assortmentName ?? ''}
      levelColumnWidth={600}
      renderRowActions={
        editCondition
          ? (row) => <ConditionsTableActions handleEdit={() => editCondition(row)} />
          : undefined
      }
    />
  );
};
